'use client'
import { useEffect, useRef, useState } from 'react'
import ScorePanel from './ScorePanel'
import type { MatchState, MatchStatus } from '@/lib/live/state'
import type { SubstitutionSwap } from '@/lib/live/substitution'

/**
 * Tabellone punteggio live — wrapper sottile su `ScorePanel` (variante `card`,
 * il componente di Dinamo). Un'unica implementazione del segnapunti per web e
 * regia. Il parametro `status` è mantenuto per retro-compatibilità con i
 * chiamanti: lo stato è già dentro `MatchState.status`.
 */
export function LiveScoreboard({
  state,
  status: _status,
  coordinates = null,
  tone = 'dark',
  substitution = null,
  commentaryGate = true,
  accentColor = '#FE7200',
  playerNames = null,
  block = null,
  ace = null,
}: {
  state: MatchState
  status: MatchStatus
  /** Gara in programma: al posto dei parziali mostra data, ora e palazzetto. */
  coordinates?: { date: string; time: string; venue: string } | null
  /** `light` per le liste su fondo chiaro (la regia lo vuole scuro). */
  tone?: 'dark' | 'light'
  /**
   * Sostituzione in onda: la scena esce→entra occupa lo spazio della squadra
   * che cambia. La pagina pubblica la riceve da `useLiveSubstitution`, la stessa
   * sorgente della console, così le due superfici mostrano lo stesso fatto.
   */
  substitution?: SubstitutionSwap | null
  /** Il commento va in onda solo dopo il punto attribuito (vedi `ScorePanel`). */
  commentaryGate?: boolean
  /** Accento del club di casa: lo stesso nei due temi (vedi `club-accent`). */
  accentColor?: string
  /** Nomi per numero dalle distinte, per il piè della sostituzione. */
  playerNames?: { home: Record<string, string>; away: Record<string, string> } | null
  block?: { team: 'home' | 'away' } | null
  /** Ace in onda: la scena si monta sulla metà di chi lo subisce. */
  ace?: { team: 'home' | 'away' } | null
}) {
  return (
    <ScorePanel
      state={state}
      variant="card"
      accentColor={accentColor}
      coordinates={coordinates}
      tone={tone}
      substitution={substitution}
      commentaryGate={commentaryGate}
      playerNames={playerNames}
      block={block}
      ace={ace}
    />
  )
}

/**
 * Lo stesso tabellone, scalato — testi compresi — dentro la sua casella.
 *
 * La casella ha la **stessa proporzione del tabellone, 5:2**: il tabellone non è
 * mai 16:9. Prima c'era una cornice 16:9 con una fascia colorata attorno alla
 * card, che lasciava bande sopra e sotto e faceva sembrare il tabellone più
 * piccolo di quello che era; i segnaposto della giornata erano già 5:2, quindi la
 * griglia mescolava due proporzioni.
 */
export function FittedLiveScoreboard({
  state,
  className = '',
  coordinates = null,
  tone = 'dark',
  substitution = null,
  commentaryGate = true,
  accentColor = '#FE7200',
  playerNames = null,
  block = null,
  ace = null,
}: {
  state: MatchState
  className?: string
  coordinates?: { date: string; time: string; venue: string } | null
  tone?: 'dark' | 'light'
  substitution?: SubstitutionSwap | null
  commentaryGate?: boolean
  accentColor?: string
  playerNames?: { home: Record<string, string>; away: Record<string, string> } | null
  block?: { team: 'home' | 'away' } | null
  /** Ace in onda: la scena si monta sulla metà di chi lo subisce. */
  ace?: { team: 'home' | 'away' } | null
}) {
  const viewportRef = useRef<HTMLElement>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const viewport = viewportRef.current
    const board = boardRef.current
    if (!viewport || !board) return
    const fit = () => {
      if (!board.offsetWidth || !board.offsetHeight) return
      const next = Math.min(viewport.clientWidth / board.offsetWidth, viewport.clientHeight / board.offsetHeight)
      setScale((previous) => Math.abs(previous - next) > 0.001 ? next : previous)
    }
    const observer = new ResizeObserver(fit)
    observer.observe(viewport)
    observer.observe(board)
    fit()
    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={viewportRef}
      aria-label="Tabellone live"
      className={`relative w-full max-w-[24rem] aspect-[5/2] overflow-hidden rounded-xl ${className}`}
    >
      <div
        ref={boardRef}
        className="absolute left-1/2 top-1/2 w-96"
        style={{ transform: `translate(-50%, -50%) scale(${scale})` }}
      >
        {/* Larghezza nativa del tabellone (384px, la stessa del pannello): qui
            non si ridisegna a una misura diversa per poi ingrandirla, altrimenti
            gli elementi con misura fissa (status, piè di card) pesano di più in
            proporzione e le due superfici divergono. Il riquadro la scala e basta. */}
        <LiveScoreboard
          state={state}
          status={state.status}
          coordinates={coordinates}
          tone={tone}
          substitution={substitution}
          commentaryGate={commentaryGate}
          accentColor={accentColor}
          playerNames={playerNames}
          block={block}
      ace={ace}
        />
      </div>
    </section>
  )
}
